/**
 * The 5 core KPIs MarginPro gathers to configure recommendations. This
 * metadata drives every KPI input UI in the app — the onboarding gate
 * modal (components/KpiGateModal.jsx) and the KPI settings page
 * (pages/SettingsKpis.jsx) both render from this single source of truth.
 *
 * `period` describes the reporting-frequency selector shown alongside a
 * section's recurring dollar fields (`period.fields`) — e.g. is Revenue a
 * weekly, monthly, or yearly figure? Per-unit fields (variableCostPerUnit,
 * pricePerUnit) and one-time/point-in-time fields (initialInvestment,
 * cashOnHand) are left out of `period.fields` since a reporting period
 * doesn't apply to them. The backend normalizes period-tagged amounts
 * before computing anything (see backend/utils/metrics.js) so the numbers
 * stay accurate no matter which period the user picks.
 *
 * Field/section keys must stay in sync with backend/models/KpiProfile.js
 * and backend/utils/metrics.js.
 */
export const PERIOD_OPTIONS = [
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

export const KPI_SECTIONS = [
  {
    key: 'grossMargin',
    label: 'Gross Margin',
    description: 'What you keep after direct costs.',
    fields: [
      { name: 'revenue', label: 'Revenue', prefix: '$' },
      { name: 'cost', label: 'Cost of goods / services', prefix: '$' },
    ],
    period: { defaultValue: 'month', fields: ['revenue', 'cost'] },
    resultLabel: 'Gross Margin',
    resultKey: 'grossMarginPct',
    resultSuffix: '%',
  },
  {
    key: 'breakEven',
    label: 'Break-Even Point',
    description: 'The volume you need to hit before you’re profitable.',
    fields: [
      { name: 'fixedCosts', label: 'Fixed costs', prefix: '$' },
      { name: 'variableCostPerUnit', label: 'Variable cost / unit', prefix: '$' },
      { name: 'pricePerUnit', label: 'Price / unit', prefix: '$' },
    ],
    period: { defaultValue: 'month', fields: ['fixedCosts'] },
    resultLabel: 'Break-Even',
    resultKey: 'breakEvenUnits',
    resultSuffix: ' units/mo',
  },
  {
    key: 'roi',
    label: 'ROI',
    description: 'What you expect to get back on what you put in.',
    fields: [
      { name: 'initialInvestment', label: 'Initial investment', prefix: '$' },
      { name: 'projectedReturn', label: 'Projected return', prefix: '$' },
    ],
    period: { defaultValue: 'year', fields: ['projectedReturn'] },
    resultLabel: 'ROI',
    resultKey: 'roiPct',
    resultSuffix: '%',
  },
  {
    key: 'burnRate',
    label: 'Burn Rate / Cash Runway',
    description: 'How fast you’re spending, and how long you can last.',
    fields: [
      { name: 'monthlyOperatingCost', label: 'Operating cost', prefix: '$' },
      { name: 'cashOnHand', label: 'Cash on hand', prefix: '$' },
    ],
    period: { defaultValue: 'month', fields: ['monthlyOperatingCost'] },
    resultLabel: 'Cash Runway',
    resultKey: 'cashRunwayMonths',
    resultSuffix: ' months',
  },
  {
    key: 'overheadRatio',
    label: 'Overhead Ratio',
    description: 'How much of revenue goes to overhead vs. the core operation.',
    fields: [
      { name: 'overheadCost', label: 'Overhead cost', prefix: '$' },
      { name: 'revenue', label: 'Revenue', prefix: '$' },
    ],
    period: { defaultValue: 'month', fields: ['overheadCost', 'revenue'] },
    resultLabel: 'Overhead Ratio',
    resultKey: 'overheadRatioPct',
    resultSuffix: '%',
  },
];

export default KPI_SECTIONS;
